import { useState, useCallback } from 'react';

/**
 * Reusable input validation hook for form validation
 * 
 * @example Email Validation:
 * ```tsx
 * const { emailError, validateEmail, setEmailError, clearEmailError } = useEmailValidation();
 * 
 * const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const newEmail = e.target.value;
 *   setEmail(newEmail);
 *   
 *   if (newEmail) {
 *     const error = validateEmail(newEmail);
 *     setEmailError(error || '');
 *   } else {
 *     clearEmailError();
 *   }
 * };
 * 
 * // In your JSX:
 * <Input
 *   type="email"
 *   value={email}
 *   onChange={handleEmailChange}
 *   className={cn(emailError && "border-red-500")}
 * />
 * {emailError && <p className="text-sm text-red-500">{emailError}</p>}
 * ```
 * 
 * @example Phone Validation:
 * ```tsx
 * const { phoneError, validatePhone, filterPhoneInput, setPhoneError, clearPhoneError } = usePhoneValidation();
 * 
 * const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const newPhone = filterPhoneInput(e.target.value); // Filter input to only allow valid characters
 *   setPhone(newPhone);
 *   
 *   if (newPhone) {
 *     const error = validatePhone(newPhone);
 *     setPhoneError(error || '');
 *   } else {
 *     clearPhoneError();
 *   }
 * };
 * 
 * // In your JSX:
 * <Input
 *   type="tel"
 *   value={phone}
 *   onChange={handlePhoneChange}
 *   className={cn(phoneError && "border-red-500")}
 * />
 * {phoneError && <p className="text-sm text-red-500">{phoneError}</p>}
 * ```
 */

interface ValidationErrors {
    [key: string]: string;
}

interface ValidationRules {
    required?: boolean;
    email?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    custom?: (value: string) => string | null; // returns error message or null if valid
}

interface UseInputValidationReturn {
    errors: ValidationErrors;
    validateField: (fieldName: string, value: string, rules: ValidationRules) => string | null;
    validateForm: (formData: { [key: string]: string }, validationSchema: { [key: string]: ValidationRules }) => boolean;
    clearError: (fieldName: string) => void;
    clearAllErrors: () => void;
    hasErrors: boolean;
}

export const useInputValidation = (): UseInputValidationReturn => {
    const [errors, setErrors] = useState<ValidationErrors>({});

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateField = useCallback((fieldName: string, value: string, rules: ValidationRules): string | null => {
        // Required validation
        if (rules.required && (!value || value.trim() === '')) {
            return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
        }

        // Skip other validations if value is empty and not required
        if (!value || value.trim() === '') {
            return null;
        }

        // Email validation
        if (rules.email && !emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }

        // Min length validation
        if (rules.minLength && value.length < rules.minLength) {
            return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${rules.minLength} characters`;
        }

        // Max length validation
        if (rules.maxLength && value.length > rules.maxLength) {
            return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be no more than ${rules.maxLength} characters`;
        }

        // Pattern validation
        if (rules.pattern && !rules.pattern.test(value)) {
            return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} format is invalid`;
        }

        // Custom validation
        if (rules.custom) {
            const customError = rules.custom(value);
            if (customError) {
                return customError;
            }
        }

        return null;
    }, []);

    const validateForm = useCallback((formData: { [key: string]: string }, validationSchema: { [key: string]: ValidationRules }): boolean => {
        const newErrors: ValidationErrors = {};
        let hasErrors = false;

        Object.keys(validationSchema).forEach(fieldName => {
            const value = formData[fieldName] || '';
            const rules = validationSchema[fieldName];
            const error = validateField(fieldName, value, rules);
            
            if (error) {
                newErrors[fieldName] = error;
                hasErrors = true;
            }
        });

        setErrors(newErrors);
        return !hasErrors;
    }, [validateField]);

    const clearError = useCallback((fieldName: string) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    }, []);

    const clearAllErrors = useCallback(() => {
        setErrors({});
    }, []);

    const hasErrors = Object.keys(errors).length > 0;

    return {
        errors,
        validateField,
        validateForm,
        clearError,
        clearAllErrors,
        hasErrors
    };
};

// Convenience hook specifically for email validation
export const useEmailValidation = () => {
    const [emailError, setEmailError] = useState<string>('');

    const validateEmail = useCallback((email: string): string | null => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!email || email.trim() === '') {
            return 'Email is required';
        }
        
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }
        
        return null;
    }, []);

    const clearEmailError = useCallback(() => {
        setEmailError('');
    }, []);

    return {
        emailError,
        validateEmail,
        setEmailError,
        clearEmailError
    };
};

// Convenience hook specifically for phone number validation
export const usePhoneValidation = () => {
    const [phoneError, setPhoneError] = useState<string>('');

    const validatePhone = useCallback((phone: string): string | null => {
        // Remove all non-digit characters for validation
        const digitsOnly = phone.replace(/\D/g, '');
        
        if (!phone || phone.trim() === '') {
            return 'Phone number is required';
        }
        
        // Check if it has at least 10 digits (standard for most phone numbers)
        if (digitsOnly.length < 10) {
            return 'Please enter a valid phone number (at least 10 digits)';
        }
        
        // Check if it has too many digits (more than 15 is unusual)
        if (digitsOnly.length > 15) {
            return 'Phone number appears to be too long';
        }
        
        return null;
    }, []);

    // Filter phone input to only allow numbers, -, +, (), and spaces
    const filterPhoneInput = useCallback((value: string): string => {
        // Only allow digits, spaces, dashes, plus signs, and parentheses
        return value.replace(/[^\d\s\-+()]/g, '');
    }, []);

    const clearPhoneError = useCallback(() => {
        setPhoneError('');
    }, []);

    return {
        phoneError,
        validatePhone,
        filterPhoneInput,
        setPhoneError,
        clearPhoneError
    };
}; 